import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { ChevronLeft, ChevronRight, LoaderCircle, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { decryptFileBytes } from "@/features/crypto/ops";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * Renders one physical PDF page at a time. `page` is the physical page index (1-based);
 * printed page labels are shown alongside when the PDF defines them.
 */
export function PdfViewer({
  attachmentId,
  encMeta,
  page,
  onPageChange,
  labels,
}: {
  attachmentId: string;
  /** For end-to-end encrypted PDFs: decrypted here before rendering. */
  encMeta?: string | null;
  page: number;
  onPageChange(page: number): void;
  labels: Record<number, string | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rendering, setRendering] = useState(false);
  const [pageInput, setPageInput] = useState(String(page));

  useEffect(() => setPageInput(String(page)), [page]);

  useEffect(() => {
    let cancelled = false;
    let task: PDFDocumentLoadingTask | null = null;
    setDoc(null);
    setError(null);
    (async () => {
      try {
        const res = await fetch(`/api/attachments/${attachmentId}/file`, { credentials: "same-origin" });
        if (!res.ok) throw new Error(res.status === 404 ? "The PDF is not available to you." : "The PDF could not be loaded. Please try again.");
        const raw = new Uint8Array(await res.arrayBuffer());
        const data = encMeta ? await decryptFileBytes(encMeta, raw) : raw;
        task = pdfjs.getDocument({ data });
        const loaded = await task.promise;
        if (cancelled) return;
        setDoc(loaded);
      } catch (e) {
        if (cancelled) return;
        const name = (e as { name?: string }).name;
        setError(
          name === "PasswordException"
            ? "This PDF is password-protected and cannot be displayed."
            : name === "InvalidPDFException"
              ? "This file is not a valid PDF or is damaged."
              : (e as Error).message || "Could not display the PDF.",
        );
      }
    })();
    return () => {
      cancelled = true;
      if (task) void task.destroy();
    };
  }, [attachmentId, encMeta]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setWidth(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!doc || !canvasRef.current || width < 50) return;
    let task: RenderTask | null = null;
    let cancelled = false;
    const n = Math.min(Math.max(1, page), doc.numPages);
    setRendering(true);
    (async () => {
      try {
        const p = await doc.getPage(n);
        if (cancelled) return;
        const base = p.getViewport({ scale: 1 });
        const scale = (width / base.width) * zoom;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = p.getViewport({ scale: scale * dpr });
        const canvas = canvasRef.current!;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
        task = p.render({ canvas, viewport });
        await task.promise;
      } catch (e) {
        if ((e as { name?: string }).name !== "RenderingCancelledException" && !cancelled) setError("This page could not be rendered.");
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, page, width, zoom]);

  const total = doc?.numPages ?? 0;
  const label = labels[page];
  const go = (n: number) => {
    if (!total) return;
    onPageChange(Math.min(Math.max(1, n), total));
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5 text-sm" role="toolbar" aria-label="PDF navigation">
        <Button size="icon-sm" variant="outline" onClick={() => go(page - 1)} disabled={!doc || page <= 1} aria-label="Previous page">
          <ChevronLeft />
        </Button>
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(pageInput);
            if (Number.isInteger(n)) go(n);
          }}
        >
          <label htmlFor={`pdf-page-${attachmentId}`} className="sr-only">
            PDF page number
          </label>
          <Input id={`pdf-page-${attachmentId}`} className="h-8 w-14 text-center" inputMode="numeric" value={pageInput} onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))} onBlur={() => go(Number(pageInput) || page)} />
        </form>
        <Button size="icon-sm" variant="outline" onClick={() => go(page + 1)} disabled={!doc || page >= total} aria-label="Next page">
          <ChevronRight />
        </Button>
        <span className="text-muted-foreground" aria-live="polite">
          PDF page {page} of {total || "…"}
          {label ? ` · printed page ${label}` : total ? " · no printed label" : ""}
        </span>
        <span className="ml-auto flex items-center">
          <Button size="icon-sm" variant="ghost" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} aria-label="Zoom out">
            <Minus />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setZoom(1)} aria-label={`Zoom ${Math.round(zoom * 100)}% — reset to fit width`}>
            {Math.round(zoom * 100)}%
          </Button>
          <Button size="icon-sm" variant="ghost" onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))} aria-label="Zoom in">
            <Plus />
          </Button>
        </span>
      </div>
      <div ref={containerRef} className="relative min-h-40 w-full overflow-auto rounded-md border bg-muted/40">
        {error ? (
          <p className="p-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : (
          <>
            {(!doc || rendering) && (
              <p className="absolute left-2 top-2 flex items-center gap-1 rounded bg-background/90 px-2 py-1 text-xs text-muted-foreground" role="status">
                <LoaderCircle className="size-3 animate-spin" aria-hidden /> {doc ? "Rendering page…" : "Loading PDF…"}
              </p>
            )}
            <canvas ref={canvasRef} role="img" aria-label={`PDF page ${page}${label ? ` (printed page ${label})` : ""}`} className="mx-auto block bg-white" />
          </>
        )}
      </div>
    </div>
  );
}
